import { Metadata } from 'libphonenumber-js'

export type YesNo = 'yes' | 'no' | 'error'
export type Language = 'fi' | 'en'
export type LifeSituation =
  | 'working'
  | 'studying'
  | 'pension'
  | 'other'
  | 'error'
export type Gender = 'woman' | 'man' | 'other' | 'noAnswer' | 'error'
export type Age =
  | 'under18'
  | '18-21'
  | '22-29'
  | '30-55'
  | '+55'
  | 'noAnswer'
  | 'error'
export type MovingWith = 'alone' | 'asCouple' | 'asFamily' | 'asGroup' | 'error'
export type CreditStatus = 'good' | 'bad' | 'error'
export type DepositPaymentMethod =
  | 'money'
  | 'kelaObligation'
  | 'other'
  | 'error'

export type ApplicationUIData = [
  'age',
  'movingWith',
  'movingWithAmount',
  'movingWithChildrenAmount',
  'movingWithDescription',
  'movingAbout',
  'pets',
  'petType',
  'readyForMoveDate',
  'lifeSituation',
  'lifeSituationCustom',
  'depositPaymentMethod',
  'depositPaymentMethodCustom',
  'depositPaymentDescription',
  'livelihood',
  'livelihoodDescription',
  'hasFinSSN',
  'creditStatus',
  'guarantorPossible',
  'gender',
  'language',
  'notes',
  'spouseLifeSituation',
  'spouseLifeSituationCustom',
  'spouseLivelihood',
  'spouseCreditStatus'
]

/** does not include yes or no props */
export interface MultiChoiceProps {
  age: Age
  gender: Gender
  lifeSituation: LifeSituation
  movingWith: MovingWith
  depositPaymentMethod: DepositPaymentMethod
  creditStatus: CreditStatus
}

export const multiChoicePropKeys = {
  age: 'age' as const,
  gender: 'gender' as const,
  lifeSituation: 'lifeSituation' as const,
  movingWith: 'movingWith' as const,
  depositPaymentMethod: 'depositPaymentMethod' as const,
  language: 'language' as const,
  creditStatus: 'creditStatus' as const,
  spouseLifeSituation: 'spouseLifeSituation' as const,
  movingAbout: 'movingAbout' as const,
  spouseCreditStatus: 'spouseCreditStatus' as const
}

export type MultiChoicePropKey =
  | typeof multiChoicePropKeys.age
  | typeof multiChoicePropKeys.gender
  | typeof multiChoicePropKeys.lifeSituation
  | typeof multiChoicePropKeys.movingWith
  | typeof multiChoicePropKeys.depositPaymentMethod
  | typeof multiChoicePropKeys.language
  | typeof multiChoicePropKeys.creditStatus
  | typeof multiChoicePropKeys.spouseLifeSituation
  | typeof multiChoicePropKeys.movingAbout
  | typeof multiChoicePropKeys.spouseCreditStatus

export interface MainProps {
  /** apartment code */
  apartmentCode: string | null
  /** apartment listing url such as Oikotie or Vuokraovi url  */
  listingURL?: string | null
  /** sms api friendly format without + such example 3584123456789 */
  language: Language
  gender: Gender
  age: Age
  /** applicant life situation */
  lifeSituation: LifeSituation
  movingWith: MovingWith
  /** description of livelihood */
  livelihoodDescription: string | null
  pets: YesNo
  /** date applicant is ready to move. instabot format such as 2019-11-13T00:00:00.000Z */
  readyForMoveDate: string
  /** Paying deposit with */
  depositPaymentMethod: DepositPaymentMethod
  /** free text for applicant, should never error  */
  notes?: string | null
}

/**
 * Special conditions for FurtherQuestionProps:
 * Question 1 === (situation) --> furtherQuestion
 * movingWith === ‘asGroup’ → movingWithAmount
 * movingWith !=== ‘alone’ → movingWithDescription
 * pet  === ‘yes’ → petType
 * depositPaymentMethod === ‘other’ → depositPaymentDescription
 * language === 'en' -> hasFinSSN
 * language === 'fi' || (language === 'en' && hasFinSSN) -> creditStatus
 * creditStatus === ‘bad’ → collateralDescription
 * wroteNotes === 'yes' -> notes
 */
export interface FurtherQuestions {
  /** amount of people moving in */
  movingWithAmount?: number | null
  /** description of others moving in */
  movingWithDescription?: string | null
  /** pet description if pets */
  petType?: string
  /** if selected "other" method, what it means */
  depositPaymentDescription?: string | null
  /** only asked if language === 'en' */
  hasFinSSN?: YesNo
  /** if language is Fi always asked, if language === 'en' and !hasFinSSN not asked */
  creditStatus?: CreditStatus
  /** if credit is 'bad' or !hasFinSSN ask if applicant can get financial guarantor */
  guarantorPossible?: YesNo
}

export interface CustomProps extends MainProps, FurtherQuestions {}

export type Platform = 'oikotie' | 'vuokraovi' | 'tori' | 'homepage' | 'unknown'

export const LeadStatuses = {
  imported: 'imported',
  leadCreated: 'created',
  profileCompleted: 'profileCompleted',
  error: 'error'
}

export declare type LeadStatus =
  | typeof LeadStatuses.imported
  | typeof LeadStatuses.leadCreated
  | typeof LeadStatuses.profileCompleted

export interface PartnerLead {
  leadId: string
  source: string
  apartmentId: string
  apartmentCode: string
  applicationId: string
  createdAt: number
  status: LeadStatus
  metadata: Record<string, any>
  createResponse: string
  completeApplicationReminders?: Array<number>
}

export interface InstabotApplicationData extends CustomProps {
  name: string
  email: string
  phone: string
  platform: Platform
  objectId?: number
}

export interface ApplicationData extends InstabotApplicationData {
  adminNotes?: string | null
  lifeSituationCustom?: string | null
}

export const ApplicationStatus = {
  created: 'created' as const,
  rejected: 'rejected' as const,
  /** If applicant pressed not interested button / link from notification.
   * Or Dwellet staff updates status to notInterested based on followUp */
  notInterested: 'notInterested' as const,
  accepted: 'accepted' as const,
  /** if application press no availabe times button from reservation tool */
  noAvailableTimes: 'noAvailableTimes' as const,
  /** Dwellet staff can set application to "manual" status when staff controls application process */
  manual: 'manual' as const,
  showingReserved: 'showingReserved' as const,
  /** Dwellet staff marks application to wantsContract status  */
  wantsContract: 'wantsContract' as const,
  tenant: 'tenant' as const
}

export type Status = typeof ApplicationStatus[keyof typeof ApplicationStatus]

export interface CreateApplicationData extends ApplicationData {
  status: Status
  apartmentUserId: string
}

export declare type PaymentStatus =
  | 'new'
  | 'ok'
  | 'fail'
  | 'pending'
  | 'delayed'

export interface Payment {
  transactionId: string
  href: string
  reference: string
  providers: any[]
  status: PaymentStatus
}
export interface ApplicationMedia {
  key: string
  name: string
  url?: string
}

export type UpdateByCodeStatus =
  | typeof ApplicationStatus.notInterested
  | typeof ApplicationStatus.noAvailableTimes

export interface ApplicationMetadata {
  created: boolean
  rejected?: boolean
  // rejectedSmsSent has a different naming, due to it being the only one where we never want to send reminder SMS
  rejectedSmsSent?: boolean
  accepted?: boolean
  manual?: boolean
  showingReserved?: boolean
  tenant?: boolean
  hasGoodCredit?: boolean
}
export interface Application {
  /** primary key */
  readonly apartmentId: string
  /** sort key */
  readonly applicationId: string
  /** Global secondary index */
  code: string
  /** Global secondary index */
  status: Status
  /** save status application was in when rejected */
  rejectedAtStatus?: Status
  /** userId of user who created the apartment (sort key of apartments table) */
  apartmentUserId: string
  showingId?: string
  createdAt: number
  updatedAt?: number
  statusUpdatedAt?: number
  data: ApplicationData
  metadata: ApplicationMetadata
  /** Admin can add custom message to application that is visible to landlord */
  message?: string
  /** Applicant can add custom message to application that is visible to landlord */
  applicantMessage?: string | null
  /** Is landLord watch user phone number */
  phoneShown?: boolean
  leadId?: string
  partnerLead?: PartnerLead
  payment?: Payment
}

/** Applications by status used in applications view */
export interface ApplicationsByStatus {
  created: Application[]
  rejected: Application[]
  noAvailableTimes: Application[]
  accepted: Application[]
  showingReserved: Application[]
  /** all other visible applications */
  other: Application[]
}

export type ApplicationListCase =
  | 'received'
  | 'rejected'
  | 'accepted'
  | 'showingReserved'

export interface UpdateApplicationStatusParams {
  message?: string
  status: 'manual' | 'wantsContract' | 'tenant'
}

export interface CensoredApplication {
  applicationId: string
  name: string
  status: Status
}

export interface CreateInstantRentApplicationData {
  apartmentCode: string
  language: string
}
export interface InstantRentApplication {
  createdAt: number
  metadata: Metadata
  confirmation?: any
  updatedAt?: number
}

export interface InstantRentAuthInformation {
  applicationCode: string
  apartmentCode: string
  readyForMoveDate: string | Date
  movingWithAmount: number | any
  acceptedTc: boolean
  language: string
  addApplicant: boolean
}

export interface InstantRentUserInformation {
  email: string
  phoneNumber: string
  postalCode: string
  postalArea: string | any
  address: string
}

export interface CreditCheckAuthBody {
  oauthState: string
  authorizationCode: string
}
